import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedStat — Number / value that counts up from 0 when scrolled into view.
 *
 * Props:
 *  - value: number (e.g. 2, 100, 24)
 *  - suffix: string after number (e.g. " hrs", "%", "/7")
 *  - prefix: string before number (e.g. "Top ")
 *  - duration: ms to animate (default 1500)
 *
 * If value isn't numeric, just shows the string with a pop entrance.
 */
export default function AnimatedStat({ value, prefix = "", suffix = "", duration = 1500, className = "", testid }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(typeof value === "number" ? 0 : value);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (typeof value !== "number") return;
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            setStarted(true);
            const start = performance.now();
            const animate = (t) => {
              const elapsed = t - start;
              const p = Math.min(1, elapsed / duration);
              const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
              setDisplay(Math.round(eased * value));
              if (p < 1) requestAnimationFrame(animate);
              else setDisplay(value);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [value, duration, started]);

  return (
    <span ref={ref} data-testid={testid} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
